import { useEffect, useRef, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'meridian.pwa.install-dismissed-at';
const DISMISS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function isRecentlyDismissed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const at = Number(raw);
  return Number.isFinite(at) && Date.now() - at < DISMISS_WINDOW_MS;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

// Customers open magic-link quote pages (/q/:token) — never offer them the internal portal PWA.
function isCustomerSurface(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/q/');
}

export interface InstallPromptState {
  canInstall: boolean;
  install: () => Promise<void>;
  dismiss: () => void;
}

export function useInstallPrompt(suppressed = false): InstallPromptState {
  const [canInstall, setCanInstall] = useState(false);
  const deferred = useRef<BeforeInstallPromptEvent | undefined>(undefined);

  useEffect(() => {
    if (isStandalone() || isRecentlyDismissed() || isCustomerSurface()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      deferred.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };
    const onInstalled = () => {
      deferred.current = undefined;
      setCanInstall(false);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  return {
    canInstall: canInstall && !suppressed,
    install: async () => {
      const e = deferred.current;
      if (!e || suppressed) return;
      await e.prompt();
      await e.userChoice;
      deferred.current = undefined;
      setCanInstall(false);
    },
    dismiss: () => {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
      deferred.current = undefined;
      setCanInstall(false);
    },
  };
}
